import { useEffect, useRef, useState, useCallback } from "react";
import { Sidebar } from "@/components/Sidebar";
import { SearchBar } from "@/components/SearchBar";
import { ChatThread, ThreadMessage, SourceItem } from "@/components/ChatThread";
import { SourcesPanel } from "@/components/SourcesPanel";
import { dedupeSources } from "@/lib/sources";
import { AccountPanel } from "@/components/AccountPanel";
import { BillingPanel } from "@/components/BillingPanel";
import { LibraryPanel } from "@/components/LibraryPanel";
import { ImagePanel } from "@/components/ImagePanel";
import { MobilePanel } from "@/components/MobilePanel";
import { ReportModal } from "@/components/ReportModal";
import { Tip } from "@/components/Tip";
import { ProjectsPage } from "@/components/projects/ProjectsPage";
import { ProjectPage } from "@/components/projects/ProjectPage";
import { ProjectCreateDialog } from "@/components/projects/ProjectCreateDialog";
import { AgentsPage } from "@/components/agents/AgentsPage";
import { DocsView } from "@/components/docs/DocsView";
import { ImageWorkspacePage } from "@/components/images/ImageWorkspacePage";
import { currentDocsHash, docsArticleHash, setDocsHash } from "@/lib/docs/deepLink";
import { GuideAssistant } from "@/components/guide/GuideAssistant";
import { ChevronDown, Menu, Plus } from "lucide-react";
import {
  fetchChats,
  fetchMessages,
  createChat,
  deleteChat,
  updateChat,
  streamMessage,
  Chat,
  UserInfo,
  SelectorModel,
  ApiError,
  AttachmentPayload,
  WebSearchMode,
  WebStatusPayload,
  getPreferences,
  putPreferences,
} from "@/lib/api";
import {
  fetchProjects,
  createProjectChat,
  Project,
} from "@/lib/api/projects";

interface ChatPageProps {
  onLogout: () => void;
  // F2: the authenticated user (from /api/users/me) — source of the
  // entitlement-filtered model catalog (F1) and billing/account panels.
  user: UserInfo;
  onUserUpdated: (u: UserInfo) => void;
}

// The new exchange is centered in the TOP ~40% of the viewport: after send
// the scroll position settles so the user bubble + the (growing) answer sit
// around the middle of the screen, and it stays put while the user is not
// in follow mode.
// The user bubble of the new exchange settles ~35% from the top of the
// viewport: visible «in the middle», and it stays put until the user scrolls
// up or presses the down button.
const CENTER_OFFSET_RATIO = 0.35;

export function ChatPage({ onLogout, user, onUserUpdated }: ChatPageProps) {
  const [selectedModel, setSelectedModel] = useState<string>("");
  // WG-3.1 Smart Search: per-user web mode (server preference, default
  // ALWAYS per Owner 21-10) + live status of the current streaming turn's
  // search. An explicit stored AUTO/OFF is never overridden — the ALWAYS
  // default applies only when no user preference exists.
  const [webMode, setWebMode] = useState<WebSearchMode>("on");
  const [webStatus, setWebStatus] = useState<WebStatusPayload | null>(null);
  // Sources UX: right-side panel state (per-message; opening it never
  // touches chat scroll, SSE, model, or web.mode).
  const [openSourcesMsgId, setOpenSourcesMsgId] = useState<string | null>(null);
  const [sourcesHighlight, setSourcesHighlight] = useState<number | null>(null);
  useEffect(() => {
    getPreferences()
      .then((p) => setWebMode(p.web_search_mode ?? "on"))
      .catch(() => {}); // preference read failure keeps the ALWAYS default
  }, []);
  const handleWebModeChange = useCallback((mode: WebSearchMode) => {
    setWebMode(mode);
    // Persist per-user server-side; a failed write keeps the local choice
    // for this session (the server default remains ALWAYS).
    putPreferences({ web_search_mode: mode }).catch(() => {});
  }, []);
  const [isBillingOpen, setIsBillingOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isAgentsOpen, setIsAgentsOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  // IMAGES V2 (Owner 19-10): dedicated /images route. The shell stays
  // state-driven but the URL is real (pushState/popstate + nginx SPA
  // fallback — hard refresh / direct link work; no HashRouter).
  const [isImagesOpen, setIsImagesOpen] = useState(
    () => window.location.pathname === "/images"
  );
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  // Documentation (D2) — a first-class view in the state-driven shell,
  // rendered in place of the chat area (same mechanism as Projects). Usable
  // without the Guide; deep/direct links are document hashes (see deepLink.ts).
  const [isDocsOpen, setIsDocsOpen] = useState(false);
  // Mobile (< md): the sidebar becomes a full-screen drawer behind this bar.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  // ---------- Projects (Phase 4) ----------
  // View routing is state-based (existing architecture, no React Router):
  //   'chat'     — chat UI (standalone or project chat)
  //   'projects' — ProjectsPage (full list)
  //   'project'  — ProjectPage (active project)
  const [activeView, setActiveView] = useState<"chat" | "projects" | "project">("chat");
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [projectCreateOpen, setProjectCreateOpen] = useState(false);
  // D6: bumped when the Settings UI changes the persisted Guide preference so
  // the GuideAssistant re-reads it live (OFF hides the robot immediately,
  // ON restores it — S4/S5) without a logout.
  const [guideSettingsTick, setGuideSettingsTick] = useState(0);
  const bumpGuideSettings = useCallback(() => setGuideSettingsTick((t) => t + 1), []);

  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const messagesRef = useRef<ThreadMessage[]>([]);
  messagesRef.current = messages;
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const followRef = useRef(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distanceFromBottom > 160);
  }, []);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    followRef.current = true;
    setShowScrollBtn(false);
  }, []);

  // Scroll handler: follow mode ends the moment the user scrolls up;
  // the button is visible only when there IS content below the viewport.
  // In center mode the user takes over as soon as they scroll AWAY from the
  // pinned spot — the re-pin on the next delta must not yank them back.
  const handleChatScroll = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > 80) followRef.current = false;
    if (
      centerModeRef.current &&
      Math.abs(el.scrollTop - pinTargetRef.current) > 12
    ) {
      centerModeRef.current = false;
      followRef.current = false;
    }
    updateScrollState();
  }, [updateScrollState]);

  // Distinguishes «the user opened an existing chat» (scroll to bottom +
  // follow) from «a new exchange was just sent» (the center-mode effect
  // owns the position; a brand-new chat may just have appeared here).
  const chatOpenedRef = useRef(false);

  // On send: pin the new exchange so the user bubble sits ~35% from the top
  // and STOP auto-following — the position holds while the answer grows
  // (the user scrolls up or presses «вниз» to take over).
  const centerModeRef = useRef(false);
  // The scrollTop that the last center-mode pin aimed for — the scroll
  // handler compares the actual position against it to detect a manual
  // takeover (any scroll away from the pinned spot exits center mode).
  const pinTargetRef = useRef(-1);
  const centerNewExchange = useCallback(() => {
    centerModeRef.current = true;
    followRef.current = false;
  }, []);

  // Pin helper: keep the newest user bubble ~35% from the top of the
  // viewport. Called on every streaming delta in center mode — the first
  // pin may be clamped to the (still short) scroll range, and as the answer
  // grows the re-pin settles the bubble into the target position.
  const pinNewExchange = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const lastUser = [...messagesRef.current].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    const bubble = el.querySelector<HTMLElement>(`[data-msg-id="${lastUser.id}"]`);
    if (!bubble) return;
    // offsetTop is content-coordinate (offsetParent = the relative scroll
    // container). Clamp to the actual scroll range so short content never
    // overshoots (the CSS my-auto wrapper centers it in that case).
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const desired = Math.max(0, bubble.offsetTop - el.clientHeight * CENTER_OFFSET_RATIO);
    const target = Math.min(maxScroll, desired);
    pinTargetRef.current = target;
    el.scrollTop = target;
  }, []);

  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;

    // 1) User opened an existing chat and its messages just loaded:
    //    show the latest message (scroll to bottom) and follow until the
    //    user scrolls up. pb-80 keeps the last message above the composer.
    if (chatOpenedRef.current) {
      chatOpenedRef.current = false;
      followRef.current = true;
      centerModeRef.current = false;
      el.scrollTop = el.scrollHeight;
      updateScrollState();
      return;
    }

    // 2) New exchange (send): keep the user bubble ~35% from the top while
    //    the answer streams (re-pinned on every delta below).
    const grew = messages.length > prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;
    if (!grew) return;
    if (centerModeRef.current) {
      requestAnimationFrame(() => pinNewExchange());
      return;
    }
    // 3) Messages grew outside center mode (e.g. a quick re-send) — keep
    //    it in view.
    el.scrollTop = el.scrollHeight;
  }, [messages, updateScrollState, pinNewExchange]);

  // During streaming: in follow mode stick to the bottom; in center mode
  // re-pin the user bubble so it settles ~35% from the top as the answer
  // grows (the first pin may be clamped to the still-short scroll range).
  const prevContentRef = useRef(0);
  useEffect(() => {
    const last = messages[messages.length - 1];
    const len = last && last.role === "assistant" ? last.content.length : 0;
    const grew = len > prevContentRef.current;
    prevContentRef.current = len;
    if (!grew) return;
    if (centerModeRef.current) {
      requestAnimationFrame(() => pinNewExchange());
    } else if (followRef.current) {
      const el = chatScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    updateScrollState();
  }, [messages, updateScrollState, pinNewExchange]);

  // activeChatId changed: a chat was opened explicitly (bottom+follow is
  // applied by the messages effect once the history lands) or a brand-new
  // chat appeared after a send (center mode owns the position — no force).
  useEffect(() => {
    if (!chatOpenedRef.current) followRef.current = false;
    updateScrollState();
  }, [activeChatId, updateScrollState]);

  // Load chats once (F10: limit=100, decided)
  useEffect(() => {
    fetchChats().then(setChats).catch(() => {});
  }, []);

  // ---------- GLOBAL NAVIGATION CONTRACT (app-shell level) ----------
  // The Sidebar's top-level entries must work from ANY mounted view — not
  // only while the chat view is mounted. /images is the only URL-routed
  // section: leaving it means closing the workspace AND pushing the URL
  // back to "/" so browser history stays honest (pushState/popstate +
  // nginx SPA fallback; no React Router). The full-area docs view is the
  // other view that hides the rest of the shell. `exitToShell()` is the
  // single guard every sidebar action runs first — pages never
  // re-implement this, and there is exactly one /images exit implementation
  // (leaveImages).
  const leaveImages = useCallback(() => {
    setIsImagesOpen(false);
    if (window.location.pathname === "/images") {
      window.history.pushState({ view: "chat" }, "", "/");
    }
  }, []);

  const exitToShell = useCallback(() => {
    if (isImagesOpen) leaveImages();
    if (isDocsOpen) setIsDocsOpen(false);
  }, [isImagesOpen, isDocsOpen, leaveImages]);

  const handleOpenSources = useCallback(
    (messageId: string | null, _sources: SourceItem[], highlightIndex: number | null = null) => {
      if (messageId === null) {
        setOpenSourcesMsgId(null);
        setSourcesHighlight(null);
        return;
      }
      // Streaming-safe: the panel list is DERIVED from message state
      // (openSourcesDerived below), so citations arriving incrementally
      // update it without duplicates or index jumps.
      setOpenSourcesMsgId(messageId);
      setSourcesHighlight(highlightIndex);
    },
    []
  );

  // Derived panel sources: deduped, order-stable, aligned with [n] markers.
  const openSourcesDerived = dedupeSources(
    openSourcesMsgId ? messages.find((m) => m.id === openSourcesMsgId)?.sources : undefined
  );

  const openChat = useCallback(async (chatId: string) => {
    // Explicit chat open (sidebar click): scroll to bottom + follow.
    // The [activeChatId] effect reads this ref on its first run for this chat.
    exitToShell();
    chatOpenedRef.current = true;
    setActiveChatId(chatId);
    setActiveView("chat");
    // Sources panel is per-message — never carry it across chat switches.
    setOpenSourcesMsgId(null);
    setSourcesHighlight(null);
    try {
      const msgs = await fetchMessages(chatId);
      setMessages(
        msgs.map((m) => ({
          id: m.id,
          role: m.role === "assistant" ? "assistant" : "user",
          content: m.content,
          model: m.model || undefined,
          createdAt: m.created_at || undefined,
          attachments: m.attachments,
          sources: m.sources && m.sources.length > 0 ? m.sources : undefined,
        }))
      );
    } catch {
      setMessages([]);
    }
  }, [exitToShell]);

  const handleNewThread = useCallback(async () => {
    abortRef.current?.abort();
    // Global nav contract: «Новая задача» is an ACTION, not a destination —
    // it must leave any routed section (/images, docs) so the new chat is
    // actually visible. This is the single authoritative implementation of
    // the New Chat action; the sidebar never re-implements it.
    exitToShell();
    try {
      const chat = await createChat("Новая задача", selectedModel || undefined);
      setChats((prev) => [chat, ...prev]);
      setActiveChatId(chat.id);
      setActiveView("chat");
      setActiveProjectId(null);
      setMessages([]);
    } catch {
      setActiveChatId(null);
      setActiveView("chat");
      setMessages([]);
    }
  }, [selectedModel, exitToShell]);

  // ---------- Projects (Phase 4) — data + handlers ----------
  // Single source of truth for projects (sidebar list + ProjectsPage share it).
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      setProjects(await fetchProjects({ limit: 200 }));
    } catch {
      setProjectsError("Не удалось загрузить проекты");
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // Load projects once on mount (real API, no mock data).
  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  // "Open a project" from the sidebar: project page (no chat selected).
  // Global nav contract: leave any routed section (e.g. /images) first so
  // the project view is actually visible.
  const handleProjectSelect = useCallback((id: string) => {
    exitToShell();
    setActiveProjectId(id);
    setActiveView("project");
  }, [exitToShell]);

  // "Проекты" nav: full ProjectsPage (same leave-first contract).
  const handleOpenProjects = useCallback(() => {
    exitToShell();
    setActiveView("projects");
  }, [exitToShell]);

  // "Документация" nav (D2): open the Documentation view. The view itself
  // reads the current deep-link hash (see DocsView + deepLink.ts); opening via
  // the nav resets it to the landing unless a #/docs/<slug> deep link is
  // already present.
  const handleOpenDocs = useCallback(() => {
    // Global nav contract: the docs view replaces the whole main area —
    // leave /images first so closing docs returns to the chat, not to a
    // resurrected images workspace under a stale URL.
    exitToShell();
    setIsDocsOpen(true);
  }, [exitToShell]);

  // Exit Documentation back to the chat view (clears the docs deep link so a
  // later reload does not re-enter docs unexpectedly).
  const handleExitDocs = useCallback(() => {
    setIsDocsOpen(false);
    try {
      window.history.pushState(null, "", window.location.pathname + window.location.search);
    } catch {
      window.location.hash = "";
    }
  }, []);

  // D6: RealityAI Guide → open the Documentation view, optionally to a
  // specific article slug (canonical internal navigation, not a new route).
  const handleOpenDocsFromGuide = useCallback((slug?: string) => {
    if (slug) setDocsHash(docsArticleHash(slug));
    exitToShell();
    setIsDocsOpen(true);
  }, [exitToShell]);

  // Direct-link / reload restore (D2-A7): if the app is (re)loaded with a docs
  // deep link in the URL hash, open the Documentation view on mount. Only runs
  // once; the DocsView component then resolves the exact article from the hash.
  useEffect(() => {
    if (currentDocsHash()) setIsDocsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- /images URL-routed section (IMAGES V2) ----------
  // pushState/popstate keeps browser back/forward correct; the nginx SPA
  // fallback serves index.html for a hard refresh on /images.
  const handleOpenImages = useCallback(() => {
    setIsImagesOpen(true);
    setIsDocsOpen(false);
    if (window.location.pathname !== "/images") {
      window.history.pushState({ view: "images" }, "", "/images");
    }
    closeMobileNav();
  }, [closeMobileNav]);

  // Back/forward + direct entry converge the view with the URL.
  useEffect(() => {
    const onPop = () => {
      setIsImagesOpen(window.location.pathname === "/images");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Back from a project view: return to the chat view (an open chat, if any,
  // stays selected). activeProjectId is kept — the sidebar still highlights
  // the active project.
  const handleProjectBack = useCallback(() => {
    setActiveView("chat");
  }, []);

  // Open a chat from the ProjectPage Chats tab.
  // "__new_in_<projectId>" = create a new chat inside that project first
  // (real POST /api/projects/{id}/chats — the single chat pipeline).
  const handleOpenProjectChat = useCallback(
    async (chatId: string) => {
      if (chatId.startsWith("__new_in_")) {
        const projectId = chatId.slice("__new_in_".length);
        try {
          const chat = await createProjectChat(projectId, undefined, selectedModel || undefined);
          // Do NOT blindly prepend to the global list: a project chat belongs
          // to the global list ONLY when its project has visibility ON.
          // Re-fetch so the sidebar reflects the visibility model correctly.
          // (The chat itself opens via activeChatId + fetchMessages below.)
          void fetchChats().then(setChats).catch(() => {});
          chatOpenedRef.current = true;
          setActiveChatId(chat.id);
          setMessages([]);
          setActiveProjectId(projectId);
          setActiveView("chat");
        } catch {
          /* the ProjectPage stays open; the user can retry */
        }
        return;
      }
      chatOpenedRef.current = true;
      setActiveChatId(chatId);
      setActiveView("chat");
    },
    [selectedModel],
  );

  // Create Project dialog success: server-confirmed project only — update the
  // list, set it active, open the project page. No full-page reload.
  const handleProjectCreated = useCallback(
    (project: Project) => {
      setProjectCreateOpen(false);
      setProjects((prev) => [project, ...prev]);
      setActiveProjectId(project.id);
      setActiveView("project");
    },
    [],
  );

  // F1 (decision #2): derive the model selector from /api/users/me —
  // entitlement-filtered `available_models.llm` + B1 `model_display_names`.
  const models: SelectorModel[] = (user.available_models?.llm ?? []).map((name) => ({
    name,
    display: user.model_display_names?.[name] || name,
    // free-model heuristic: the FREE model family is `gpt-oss-20b*`
    // (available_models is a plain list; is_free is not exposed in it).
    isFree: name.toLowerCase().includes("gpt-oss"),
  }));

  // Keep the selected model valid when the entitlement-filtered catalog
  // changes (e.g. after activation). Default to the first (free-first) model.
  useEffect(() => {
    if (models.length === 0) return;
    setSelectedModel((prev) => (models.some((m) => m.name === prev) ? prev : models[0].name));
  }, [models]);

  const handleSearch = useCallback(
    async (query: string, attachments?: AttachmentPayload[]) => {
      if (isLoading) return;
      setIsLoading(true);

      let chatId = activeChatId;
      if (!chatId) {
        try {
          const chat = await createChat(query.slice(0, 60) || "New Chat", selectedModel || undefined);
          chatId = chat.id;
          setActiveChatId(chat.id);
          setChats((prev) => [chat, ...prev]);
        } catch {
          setIsLoading(false);
          return;
        }
      }

      const userMsg: ThreadMessage = {
        id: `u-${Date.now()}`,
        role: "user",
        content: query,
        attachments: attachments,
      };
      const assistantMsg: ThreadMessage = {
        id: `a-${Date.now()}`,
        role: "assistant",
        content: "",
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      // Center the new exchange — auto-follow stays OFF.
      centerNewExchange();

      const controller = new AbortController();
      abortRef.current = controller;
      // WG-3.1 §29: with mode=on a search is guaranteed — show the honest
      // "searching" status immediately (no fake progress). With auto the
      // router decides server-side; the web_status SSE event then reports
      // the actual outcome (found N / degraded) — never a guess.
      // WG-4B §56: an explicit URL + web not OFF -> the Reader will open it;
      // show the honest "reading" status immediately (deterministic client
      // URL check mirrors the server detector; the SSE web_status event then
      // reports the real outcome).
      setWebStatus(
        webMode === "on"
          ? { status: "enabled" }
          : webMode !== "off" && /https?:\/\/\S/.test(query)
            ? { status: "enabled", operation: "reader" }
            : null,
      );

      try {
        let fullText = "";
        await streamMessage(
          chatId,
          query,
          selectedModel || undefined,
          {
            onDelta: (text) => {
              fullText += text;
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantMsg.id ? { ...m, content: fullText } : m))
              );
            },
            onWebStatus: (data) => {
              setWebStatus(data);
              // ALWAYS-mode failure: the user must SEE that web did not run
              // (§31) — never present old knowledge as a web result.
              if (
                (data?.status === "degraded" ||
                  data?.status === "unavailable" ||
                  data?.status === "off") &&
                data?.notice
              ) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id && !m.content
                      ? { ...m, content: `🌐 ${data.notice}` }
                      : m
                  )
                );
              }
            },
            onCitations: (items) => {
              if (items && items.length > 0) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id && !m.createdAt
                      ? { ...m, content: fullText, sources: items }
                      : m
                  )
                );
              }
            },
            onDone: () => {
              centerModeRef.current = false;
              followRef.current = false;
              fetchChats().then(setChats).catch(() => {});
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id && !m.createdAt
                    ? { ...m, createdAt: new Date().toISOString() }
                    : m
                )
              );
            },
            onError: (data) => {
              const msg = data?.message || "Ошибка генерации";
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id
                    ? { ...m, content: m.content || `⚠️ ${msg}` }
                    : m
                )
              );
            },
          },
          controller.signal,
          attachments,
          webMode
        );
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id && !m.content
                ? { ...m, content: "⏹ Генерация остановлена" }
                : m
            )
          );
        } else if (err instanceof ApiError) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? { ...m, content: `⚠️ ${err.message}` }
                : m
            )
          );
        } else {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? { ...m, content: `⚠️ Сетевая ошибка. Попробуйте ещё раз.` }
                : m
            )
          );
        }
      } finally {
        setIsLoading(false);
        abortRef.current = null;
        // WG-3.1: the search status belongs to the finished turn only.
        setWebStatus(null);
        updateScrollState();
      }
    },
    [activeChatId, selectedModel, isLoading, centerNewExchange, updateScrollState, webMode]
  );

  // F3 (decision #13): UI trigger for the abort (previously dead code).
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleDeleteChat = useCallback(
    async (chatId: string) => {
      try {
        await deleteChat(chatId);
        setChats((prev) => prev.filter((c) => c.id !== chatId));
        if (activeChatId === chatId) {
          setActiveChatId(null);
          setMessages([]);
        }
      } catch {
        // ignore
      }
    },
    [activeChatId]
  );

  const handleRenameChat = useCallback(async (chatId: string, title: string) => {
    try {
      const updated = await updateChat(chatId, { title });
      setChats((prev) => prev.map((c) => (c.id === chatId ? updated : c)));
    } catch {
      // ignore
    }
  }, []);

  const handleTogglePin = useCallback(async (chatId: string, pinned: boolean) => {
    try {
      const updated = await updateChat(chatId, { is_pinned: pinned });
      setChats((prev) =>
        [updated, ...prev.filter((c) => c.id !== chatId)].sort((a, b) => {
          if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
          return 0;
        })
      );
    } catch {
      // ignore
    }
  }, []);

  return (
    <div className="flex h-dvh bg-app text-primary font-sans overflow-hidden">
      <Sidebar
        onNewThread={handleNewThread}
        chats={chats}
        activeChatId={activeChatId}
        onChatSelect={openChat}
        onRenameChat={handleRenameChat}
        onTogglePin={handleTogglePin}
        onDeleteChat={handleDeleteChat}
        tariffCode={user.tariff_code}
        userName={user.name || user.username}
        onNavigate={closeMobileNav}
        mobileOpen={mobileNavOpen}
        onMobileClose={closeMobileNav}
        onOpenBilling={() => {
          exitToShell();
          setIsBillingOpen(true);
        }}
        onOpenAccount={() => {
          exitToShell();
          setIsAccountOpen(true);
        }}
        onOpenAgents={() => {
          exitToShell();
          setIsAgentsOpen(true);
        }}
        onOpenLibrary={() => {
          exitToShell();
          setIsLibraryOpen(true);
        }}
        onOpenImage={handleOpenImages}
        onOpenMobile={() => {
          exitToShell();
          setIsMobileOpen(true);
        }}
        onOpenSupport={() => {
          exitToShell();
          setIsSupportOpen(true);
        }}
        onOpenDocs={handleOpenDocs}
        onLogout={onLogout}
        activeSection={
          isDocsOpen
            ? "docs"
            : isImagesOpen
              ? "images"
              : activeView === "projects"
                ? "projects"
                : activeView === "project"
                  ? "project"
                  : isAgentsOpen
                    ? "agents"
                    : "chat"
        }
        projects={projects}
        projectsLoading={projectsLoading}
        projectsError={projectsError}
        activeProjectId={activeProjectId}
        onProjectSelect={handleProjectSelect}
        onOpenProjects={handleOpenProjects}
        onProjectCreate={() => setProjectCreateOpen(true)}
        onProjectsRefresh={() => void loadProjects()}
        onProjectArchived={() => void loadProjects()}
        onProjectRestored={() => void loadProjects()}
      />

      <main className="flex-1 flex flex-col relative overflow-hidden">
        {isDocsOpen ? (
          <DocsView onExit={handleExitDocs} />
        ) : isImagesOpen ? (
          <ImageWorkspacePage />
        ) : (
          <>
        {/* Mobile top bar: drawer trigger + brand + new-thread. Hidden on md+
            where the desktop rail/sidebar is visible instead. */}
        <div className="md:hidden flex items-center gap-2 px-3 h-14 border-b border-hairline bg-app shrink-0">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
            aria-label="Меню"
          >
            <Menu size={18} />
          </button>
          <img
            src="/icons/brand-r-64.png"
            alt="RealityAI"
            className="w-7 h-7 select-none"
            draggable={false}
          />
          <span className="text-sm font-medium text-primary">RealityAI</span>
          <div className="flex-1" />
          <button
            onClick={handleNewThread}
            className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
            aria-label="Новая задача"
          >
            <Plus size={18} />
          </button>
        </div>

        {/* ---- Projects views (Phase 4) — replace the chat area ---- */}
        {activeView === "projects" ? (
          <ProjectsPage
            projects={projects}
            loading={projectsLoading}
            error={projectsError}
            onRefresh={() => void loadProjects()}
            onProjectSelect={handleProjectSelect}
            onCreateClick={() => setProjectCreateOpen(true)}
          />
        ) : activeView === "project" && activeProjectId ? (
          <ProjectPage
            projectId={activeProjectId}
            onBack={handleProjectBack}
            onOpenChat={(chatId) => void handleOpenProjectChat(chatId)}
            onProjectsChanged={() => {
              void loadProjects();
              // Project chat visibility / lifecycle can change the GLOBAL list
              // (show_project_chats_in_global_chat, rename, archive) — refresh
              // chats too so the Sidebar reflects it immediately.
              fetchChats().then(setChats).catch(() => {});
            }}
            selectedModel={selectedModel}
          />
        ) : (
        (() => {
          const searchBar = (
            <SearchBar
              onSearch={handleSearch}
              isLoading={isLoading}
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              models={models}
              onStop={handleStop}
              onLibraryChanged={() => {}}
              webMode={webMode}
              onWebModeChange={handleWebModeChange}
              webStatus={webStatus}
            />
          );
          const disclaimer = (
            <div className="flex items-center justify-center mt-3">
              <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full text-[10px] md:text-sm text-tertiary">
                AI может ошибаться — проверяйте важную информацию.
              </span>
            </div>
          );

          return (
            <>
              {/* Chat Area */}
              <div ref={chatScrollRef} onScroll={handleChatScroll} className="flex-1 overflow-y-auto relative">
                <div className="flex flex-col min-h-full">
                {/* pb-64 (mobile) / pb-80 (desktop) >= composer overlay height
                    so the last message never ends up hidden behind the pinned composer. */}
                <div className="my-auto w-full max-w-3xl mx-auto px-4 py-8 pb-64 md:pb-80">
                  {messages.length > 0 ? (
                    <ChatThread
                      messages={messages}
                      isLoading={isLoading}
                      openSourcesMsgId={openSourcesMsgId}
                      onOpenSources={handleOpenSources}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center min-h-[50vh] md:min-h-[70vh] animate-in fade-in zoom-in duration-500">
                      <div className="flex flex-col items-center gap-4 mb-8">
                        <h1 className="text-4xl md:text-6xl font-serif tracking-tight flex items-center gap-2.5 select-none">
                          <span className="text-accent glow-cyan">Reality</span>
                          <span className="text-primary glow-white">AI</span>
                        </h1>
                        <p className="text-sm md:text-base text-secondary text-center leading-relaxed max-w-md">
                          Бесплатная LLM уже доступна.
                          <br />
                          Попробуйте GPT-OSS прямо сейчас в чате — бесплатно, без подписки.
                        </p>
                      </div>
                      <div className="w-full max-w-3xl">
                        {searchBar}
                        {disclaimer}
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </div>

              {/* Composer area (pinned bottom) — the scroll-to-bottom button
                  lives HERE, right above the input, not floating mid-screen. */}
              {messages.length > 0 && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-app via-app to-transparent pt-24 pb-5 px-4 z-20">
                  <div className="pointer-events-auto max-w-3xl mx-auto w-full relative">
                    {showScrollBtn && (
                      <Tip label="К последним сообщениям" side="top" className="absolute -top-12 left-1/2 -translate-x-1/2 z-30">
                        <button
                          onClick={() => scrollToBottom(true)}
                          aria-label="К последним сообщениям"
                          className="w-10 h-10 rounded-full flex items-center justify-center bg-surface border border-strongline text-secondary hover:text-primary hover:bg-hoverbg transition-all shadow-lg"
                        >
                          <ChevronDown size={20} />
                        </button>
                      </Tip>
                    )}
                    {searchBar}
                    {disclaimer}
                  </div>
                </div>
              )}
            </>
          );
        })()
        )}

        {/* F5: panels (Billing, Account, Agent ComingSoon, Library).
            «Финансы» — только через профиль (личный кабинет), не в sidebar-навигации. */}
        {isBillingOpen && (
          <BillingPanel
            user={user}
            onClose={() => setIsBillingOpen(false)}
            onUserUpdated={onUserUpdated}
          />
        )}
        {isAccountOpen && (
          <AccountPanel
            user={user}
            onClose={() => setIsAccountOpen(false)}
            onUserUpdated={onUserUpdated}
            onLogout={onLogout}
            onGuidePreferenceChange={bumpGuideSettings}
          />
        )}
        {isLibraryOpen && (
          <LibraryPanel
            user={user}
            onClose={() => setIsLibraryOpen(false)}
          />
        )}
        {isImageOpen && (
          <ImagePanel onClose={() => setIsImageOpen(false)} />
        )}
        {isMobileOpen && (
          <MobilePanel onClose={() => setIsMobileOpen(false)} />
        )}
        {isSupportOpen && (
          <ReportModal onClose={() => setIsSupportOpen(false)} />
        )}
        {/* K7: Agent surface (replaces the F8 «Агенты (скоро)» modal).
            agents_enabled=false → the page itself renders the disabled/
            ComingSoon state from the K6 404 (graceful, not an error). */}
        {isAgentsOpen && (
          <AgentsPage
            onClose={() => setIsAgentsOpen(false)}
            modelNames={models.map((m) => m.name)}
            modelDisplayNames={user.model_display_names ?? {}}
            projectNameById={(id) => projects.find((p) => p.id === id)?.name ?? null}
          />
        )}

        {/* Create Project dialog (Phase 4) — real POST /api/projects */}
        {projectCreateOpen && (
          <ProjectCreateDialog
            onClose={() => setProjectCreateOpen(false)}
            onCreated={handleProjectCreated}
          />
        )}
          </>
        )}
      </main>

      {/* Sources UX: right-side panel — side-by-side on lg+, overlay
          drawer on narrow viewports. Independent scroll; opening it
          never resets chat scroll, SSE, model, or web.mode. */}
      {activeView === "chat" && !isDocsOpen && !isImagesOpen && (
        <SourcesPanel
          sources={openSourcesDerived}
          open={openSourcesMsgId !== null}
          onClose={() => {
            setOpenSourcesMsgId(null);
            setSourcesHighlight(null);
          }}
          highlightIndex={sourcesHighlight}
        />
      )}

      {/* D6: RealityAI Guide assistant (bottom-right). Rendered at the root
          so it persists across the chat view and the Documentation view.
          Self-gates on D5 effective visibility (system flag AND user
          authorized AND user preference) — renders nothing when disabled
          (near-zero cost, no timers, no requests). */}
      <GuideAssistant
        context={{ surface: "web", platform: "web", locale: "ru" }}
        onOpenDocumentation={handleOpenDocsFromGuide}
        settingsTick={guideSettingsTick}
      />
    </div>
  );
}
